import { useCallback, useMemo } from "react";

import ActionButton from "@/components/Button/ActionButton";
import Icon from "@/components/Icon/Icon";
import HStack from "@/components/Layout/HStack";
import VStack from "@/components/Layout/VStack";
import { MenuItem } from "@/components/MenuItem";
import { TooltipWrapper } from "@/components/Tooltip/TooltipWrapper";
import { Text } from "@/components/Typography/Text";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

export type SidebarState = "open" | "closed";

export type TabItem = Readonly<{
  id: string;
  label: string;
  type?: string;
  count?: number;
  isUnderDevelopment?: boolean;
  disabled?: boolean;
}>;

// 将来的にID、ラベルが増えることを見越してObjectとして定義。
type Group = Readonly<{
  packages: TabItem[];
}>;

export type PackageSidebarProps = Readonly<{
  /** サイドバーに表示するグループ（リソースごとに区切る、間にセパレータが入ります） */
  groups: Group[];
  /** 現在アクティブな項目ID */
  activeId: string;
  /** 項目選択時のコールバック */
  onSelect: (id: string) => void;
  /** サイドバーのスタイル調整用クラス */
  className?: string;
  /** サイドバーの状態 (開閉) */
  sidebarState: SidebarState;
  /** サイドバーの状態変更時のコールバック */
  onStateChange: (state: SidebarState) => void;
  /** 開発中表示のテキスト */
  underDevelopmentText?: string;
}>;

export function PackageSidebar({
  groups,
  activeId,
  onSelect,
  className,
  sidebarState,
  onStateChange,
  underDevelopmentText,
}: PackageSidebarProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.operationPackage.sideBar",
  });
  const isOpen = sidebarState === "open";

  const localizedCloseText = t("close");

  const handleSelect = useCallback(
    (id: string) => () => {
      onSelect(id);
    },
    [onSelect],
  );

  return (
    <nav
      data-state={sidebarState}
      style={{ minWidth: isOpen ? "300px" : "48px", scrollbarGutter: "stable" }}
      className={cn(
        "h-full bg-neutral-50/60 shadow-md",
        "border-neutral-muted-border border-r",
        "overflow-hidden hover:overflow-auto",
        "transition-all duration-200",
        className,
      )}
    >
      <VStack
        className={cn(
          "gap-3 h-full",
          isOpen && "px-6 pt-7 pb-4",
          !isOpen && "px-2 py-6 justify-between",
        )}
      >
        <div className="h-full">
          <Groups
            groups={groups}
            activeId={activeId}
            isOpen={isOpen}
            onSelect={handleSelect}
            underDevelopmentText={underDevelopmentText}
          />
        </div>
        <ToggleButton
          isOpen={isOpen}
          closeText={localizedCloseText}
          onClick={() => onStateChange(isOpen ? "closed" : "open")}
          className={cn(
            "absolute",
            isOpen ? "w-[252px] bottom-4" : "w-8 bottom-6",
          )}
        />
      </VStack>
    </nav>
  );
}

function Groups({
  groups,
  activeId,
  isOpen,
  onSelect,
  underDevelopmentText,
}: {
  groups: Group[];
  activeId: string;
  isOpen: boolean;
  onSelect: (id: string) => () => void;
  underDevelopmentText?: string;
}) {
  const activeLabel = useMemo(
    () =>
      groups
        .flatMap((group) => group.packages)
        .find((item) => item.id === activeId)?.label,
    [groups, activeId],
  );

  if (!isOpen) {
    return (
      <VStack className="items-center">
        <Text variant="label" className="rounded [writing-mode:vertical-rl]">
          {activeLabel}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack className="gap-3">
      {groups.map((group, index) => (
        <VStack key={index} className="gap-3">
          {group.packages.map((item) => (
            <TabItem
              key={item.id}
              item={item}
              active={activeId === item.id}
              onSelect={onSelect(item.id)}
              underDevelopmentText={underDevelopmentText}
            />
          ))}
          {index !== groups.length - 1}
        </VStack>
      ))}
    </VStack>
  );
}

function TabItem({
  item,
  active,
  onSelect,
  underDevelopmentText,
}: {
  item: TabItem;
  active: boolean;
  onSelect: () => void;
  underDevelopmentText?: string;
}) {
  const tabItem = (
    <MenuItem
      className={cn(
        "w-full py-1.5 px-3",
        active && "bg-primary-muted-fill text-primary-on-muted-fill",
        !active &&
          !item.isUnderDevelopment &&
          !item.disabled &&
          "text-neutral-muted-on-fill",
        (item.isUnderDevelopment || item.disabled) &&
          "text-neutral-on-fill-disabled",
      )}
      active={active}
      onClick={onSelect}
      disabled={item.isUnderDevelopment || item.disabled}
    >
      <HStack className="justify-between items-center w-full">
        <Text
          variant="buttonLabel"
          className="line-clamp-2 wrap-break-word capitalize"
        >
          {item.label}
        </Text>
        {item.count != null && (
          <Text variant="buttonLabelSmall">{item.count}</Text>
        )}
      </HStack>
    </MenuItem>
  );

  return item.isUnderDevelopment && underDevelopmentText ? (
    <TooltipWrapper
      content={underDevelopmentText}
      triggerCursor="not-allowed"
      side="right"
      sideOffset={8}
      delayDuration={200}
    >
      {tabItem}
    </TooltipWrapper>
  ) : (
    tabItem
  );
}

function ToggleButton({
  isOpen,
  closeText,
  onClick,
  className,
}: {
  isOpen: boolean;
  closeText: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <ActionButton
      variant="ghost"
      className={cn(
        "bg-neutral-50/60 backdrop-blur-xs text-neutral-muted-on-fill",
        !isOpen && "px-2 w-8 h-8",
        className,
      )}
      onClick={onClick}
    >
      <HStack className={cn("w-full justify-start items-center gap-2")}>
        <Icon iconName={isOpen ? "PanelLeftCloseIcon" : "PanelLeftOpenIcon"} />
        {isOpen && (
          <Text variant="buttonLabel" className="text-neutral-muted-on-fill">
            {closeText}
          </Text>
        )}
      </HStack>
    </ActionButton>
  );
}
